import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { getResultados } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { fechaCorta, hoyGT } from '../finanzas/estados';
import { finDeMes } from '../../utils/dates';
import { exportResultadosPdf } from '../../pdf/resultados';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import './Resultados.css';

const round = (x) => Math.round((Number(x) || 0) * 100) / 100;
const iso = (d) => d.toISOString().slice(0, 10);
const weekValue = (date) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const year = d.getUTCFullYear();
  const start = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil((((d - start) / 86400000) + 1) / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
};
const datesForWeek = (value) => {
  const [year, week] = value.split('-W').map(Number);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4.getUTCDay() || 7) + 1 + (week - 1) * 7);
  const sunday = new Date(monday); sunday.setUTCDate(monday.getUTCDate() + 6);
  return { desde: iso(monday), hasta: iso(sunday) };
};
const range = (mode, month, week, date, from, to) => {
  if (mode === 'mes') return { desde: `${month}-01`, hasta: finDeMes(month) };
  if (mode === 'semana') return datesForWeek(week);
  if (mode === 'dia') return { desde: date, hasta: date };
  return { desde: from <= to ? from : to, hasta: from <= to ? to : from };
};
const cells = (d) => [toMoney(d.totalIngresos), toMoney(d.compras), toMoney(round(d.gastos + d.pagosOperativos + d.otrosEgresos)),
  toMoney(round(d.comisionTarjeta + d.comisionPedidosYa)), toMoney(d.resultado)];

export default function Resultados() {
  const { sucursales, sucursalId, me } = useSucursalActiva();
  const today = hoyGT();
  const [modo, setModo] = useState('mes');
  const [mes, setMes] = useState(today.slice(0, 7));
  const [semana, setSemana] = useState(weekValue(today));
  const [dia, setDia] = useState(today);
  const [desde, setDesde] = useState(`${today.slice(0, 7)}-01`);
  const [hasta, setHasta] = useState(today);
  const suc = sucursalId || 'all'; // sucursal del selector global
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista] = useState('dia');
  const periodo = useMemo(() => range(modo, mes, semana, dia, desde, hasta), [modo, mes, semana, dia, desde, hasta]);
  const cargar = useCallback(async () => {
    setCargando(true);
    try { setData(await getResultados({ ...periodo, sucursalId: suc || 'all' })); }
    catch (e) { setData(null); Swal.fire('Resultados', e.message || 'No se pudo cargar el reporte', 'error'); }
    finally { setCargando(false); }
  }, [periodo, suc]);
  useEffect(() => { cargar(); }, [cargar]);
  const t = data?.totales;
  const nombreSuc = suc === 'all' ? 'Todas las sucursales' : sucursales.find((s) => s.id === suc)?.nombre || 'Sucursal';
  const pdf = (imprimir) => exportResultadosPdf(data, { sucursal: nombreSuc, generadoPor: me?.username }, { imprimir });
  const tipos = (data?.tipos || []).filter((x) => vista === 'ingreso'
    ? ['ventasEfectivo','ventasTarjeta','ventasPedidosYa','otrosIngresos'].includes(x.clave)
    : ['compras','gastos','pagosOperativos','comisionTarjeta','comisionPedidosYa','otrosEgresos'].includes(x.clave));

  return <div className="prov-shell res-shell">
    <header className="prov-header"><div><h1>Resultados</h1><p>Ganancia o pérdida de la operación en el período.</p></div>
      <div className="res-actions"><button type="button" className="fx-btn" disabled={!data || cargando} onClick={() => pdf(true)}>Imprimir</button>
        <button type="button" className="fx-btn fx-btn--primary" disabled={!data || cargando} onClick={() => pdf(false)}>Descargar PDF</button></div></header>
    <div className="fx-card res-filters"><div className="res-modes" role="group" aria-label="Período">
      {[['mes','Mes'],['semana','Semana'],['dia','Día específico'],['rango','Rango']].map(([id, label]) =>
        <button key={id} type="button" className={`fx-btn ${modo === id ? 'fx-btn--primary' : ''}`} aria-pressed={modo === id} onClick={() => setModo(id)}>{label}</button>)}</div>
      <div className="res-filter-fields">
        {modo === 'mes' && <label>Mes<input type="month" value={mes} onChange={(e) => setMes(e.target.value)} /></label>}
        {modo === 'semana' && <label>Semana<input type="week" value={semana} onChange={(e) => setSemana(e.target.value)} /></label>}
        {modo === 'dia' && <label>Día<input type="date" value={dia} onChange={(e) => setDia(e.target.value)} /></label>}
        {modo === 'rango' && <><label>Desde<input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
          <label>Hasta<input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label></>}
      </div><p className="res-period">{fechaCorta(periodo.desde)} al {fechaCorta(periodo.hasta)} · {nombreSuc}</p></div>
    {cargando ? <p className="fx-empty">Cargando resultados…</p> : t && <>
      <div className={`fx-card res-hero ${t.resultado < 0 ? 'res-loss' : 'res-profit'}`}>
        <span>Resultado neto</span><strong>{t.resultado < 0 ? 'Pérdida' : 'Ganancia'}: {toMoney(Math.abs(t.resultado))}</strong>
        <small>Margen: {t.margen == null ? '—' : `${t.margen.toFixed(2)} %`}</small>
      </div>
      <div className="res-kpis">
        {[['Ventas totales',round(t.ventasEfectivo+t.ventasTarjeta+t.ventasPedidosYa)],['Compras',t.compras],
          ['Gastos',round(t.gastos+t.pagosOperativos+t.otrosEgresos)],['Comisiones',round(t.comisionTarjeta+t.comisionPedidosYa)],
          ['Total ingresos',t.totalIngresos],['Total egresos',t.totalEgresos]].map(([k, v]) =>
          <div key={k} className="fx-card fx-stat"><span>{k}</span><b>{toMoney(v)}</b></div>)}</div>
      <div className="res-columns">
        <section className="fx-card"><h2>Ingresos</h2>
          <dl>{[['Ventas en efectivo',t.ventasEfectivo],['Ventas con tarjeta',t.ventasTarjeta],['PedidosYa',t.ventasPedidosYa],
            ['Otros ingresos',t.otrosIngresos]].map(([k,v]) => <div key={k}><dt>{k}</dt><dd>{toMoney(v)}</dd></div>)}
            <div className="res-total"><dt>Total ingresos</dt><dd>{toMoney(t.totalIngresos)}</dd></div></dl></section>
        <section className="fx-card"><h2>Costos y egresos</h2>
          <dl>{[['Compras y facturas',t.compras],['Gastos',t.gastos],['Pagos operativos',t.pagosOperativos],
            ['Comisiones de tarjeta',t.comisionTarjeta],['Comisiones de PedidosYa',t.comisionPedidosYa],
            ['Otros egresos',t.otrosEgresos]].map(([k,v]) => <div key={k}><dt>{k}</dt><dd>{toMoney(v)}</dd></div>)}
            <div className="res-total"><dt>Total egresos</dt><dd>{toMoney(t.totalEgresos)}</dd></div></dl></section>
      </div>
      {data.ventasEstimadas > 0 && <p className="res-note">{data.ventasEstimadas} cierre(s) sin ventas del sistema: las ventas en efectivo se estimaron con el conteo y las salidas registradas.</p>}
      <section className="fx-card res-details"><h2>Detalle</h2><div className="res-modes" role="tablist" aria-label="Desglose">
        {[['dia','Por día'],['sucursal','Por sucursal'],['ingreso','Tipos de ingreso'],['gasto','Tipos de gasto']].map(([id,label]) =>
          <button key={id} type="button" role="tab" aria-selected={vista===id} className={`fx-btn ${vista===id?'fx-btn--primary':''}`} onClick={() => setVista(id)}>{label}</button>)}</div>
        {(vista === 'dia' || vista === 'sucursal') ? <div className="res-scroll"><table className="fx-table"><thead><tr>
          <th>{vista === 'dia' ? 'Día' : 'Sucursal'}</th>{vista === 'dia' && suc === 'all' && <th>Sucursal</th>}
          <th className="num">Ingresos</th><th className="num">Compras</th><th className="num">Gastos</th><th className="num">Comisiones</th><th className="num">Resultado</th></tr></thead>
          <tbody>{(vista === 'dia' ? data.dias : data.sucursales).map((r,i) => <tr key={i}><td>{vista === 'dia' ? fechaCorta(r.fecha) : r.sucursal}</td>
            {vista === 'dia' && suc === 'all' && <td>{r.sucursal}</td>}{cells(r).map((v,j) => <td key={j} className={`num ${j===4 && r.resultado<0?'res-negative':''}`}>{v}</td>)}</tr>)}</tbody>
          <tfoot><tr><td>Total</td>{vista === 'dia' && suc === 'all' && <td />}{cells(t).map((v,j) => <td key={j} className="num"><b>{v}</b></td>)}</tr></tfoot></table></div>
          : <div className="res-scroll"><table className="fx-table"><thead><tr><th>Tipo</th><th>Concepto</th><th className="num">Monto</th></tr></thead>
              <tbody>{tipos.map((r,i) => <tr key={i}><td>{r.clave}</td><td>{r.tipo}</td><td className="num">{toMoney(r.monto)}</td></tr>)}</tbody></table></div>}
      </section>
    </>}
  </div>;
}
